import React, { useContext, useState } from 'react';
import { Playlist } from '@/infrastructure/class/playlist';
import {
  ICreatePlaylist,
  IPlaylist,
} from '@/infrastructure/interfaces/i-playlist';
import { firebaseApp } from '@/infrastructure/firebase/firebase-app';
import { FIRESTORE_COLLECTIONS } from '@/infrastructure/enum/firestore';
import type { User } from 'firebase/auth';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import { getUniqueElements } from '@/utils/helpers/get-unique-elements';
import { removeElementsFromArray } from '@/utils/helpers/remove-elements-from-array';

const getApiClient = async () => {
  const { Api } = await import('@/api');
  return Api.getInstance();
};

const getFirestoreClient = async () => {
  const firestore = await import('firebase/firestore');
  return {
    db: firestore.getFirestore(firebaseApp),
    ...firestore,
  };
};

const playlistDocRef = (db: any, docFn: any, playlistId: string) =>
  docFn(db, FIRESTORE_COLLECTIONS.SHARED_PLAYLISTS, playlistId);

interface IPlaylistState {
  items: Playlist[];
  currentPlaylist: Playlist | null;
}

export interface IBasePlaylistDetailEffect {
  user: User;
  playlistId: string;
}

export interface ICreatePlaylistEffect {
  user: User;
  playlistName: string;
}

export interface IBasePlaylistItemsUpdateEffect {
  user: User;
  playlistId: string;
  audioSurah: AudioSurah;
}

export interface IPlaylistContext {
  playlist: IPlaylistState;
  loadPlaylists: (user: User) => void;
  getPlaylistDetail: (payload: IBasePlaylistDetailEffect) => void;
  createPlaylist: (payload: ICreatePlaylistEffect) => void;
  deletePlaylist: (payload: IBasePlaylistDetailEffect) => void;
  addToPlaylist: (payload: IBasePlaylistItemsUpdateEffect) => void;
  removeFromPlaylist: (payload: IBasePlaylistItemsUpdateEffect) => void;
}

interface IPlaylistProvider {
  children?: React.ReactNode;
}

export const PlaylistContext = React.createContext<IPlaylistContext | null>(
  null
);

const initialState = {
  items: [],
  currentPlaylist: null,
} as IPlaylistState;

const PlaylistProvider: React.FC<IPlaylistProvider> = ({ children }) => {
  const [playlistState, setPlaylist] = useState<IPlaylistState>(initialState);

  const setPlaylists = (playlist: IPlaylist[]) => {
    const items = playlist.map((playlist) => new Playlist(playlist));
    setPlaylist({
      ...playlistState,
      items,
    });
  };

  const addPlaylist = (playlist: IPlaylist) => {
    setPlaylist({
      ...playlistState,
      items: [...playlistState.items, new Playlist(playlist)],
    });
  };

  const setCurrentPlaylist = (playlist: IPlaylist) => {
    setPlaylist({
      ...playlistState,
      currentPlaylist: new Playlist(playlist),
    });
  };

  const loadPlaylists = async (user: User) => {
    try {
      const { db, collection, getDocs, query, where } =
        await getFirestoreClient();
      const res: IPlaylist[] = [];
      const collectionRef = collection(
        db,
        FIRESTORE_COLLECTIONS.SHARED_PLAYLISTS
      );
      const q = query(collectionRef, where('ownerID', '==', user.uid));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        res.push({
          ...doc.data(),
          id: doc.id,
        } as IPlaylist);
      });
      setPlaylists(res);
    } catch (error) {
      console.error(error);
    }
  };

  const getPlaylistDetail = async ({
    user,
    playlistId,
  }: IBasePlaylistDetailEffect) => {
    try {
      const { db, doc, getDoc } = await getFirestoreClient();
      const docRef = playlistDocRef(db, doc, playlistId);
      const documentData = await getDoc(docRef);
      const data = documentData.data() as IPlaylist;
      if (documentData && data.ownerID === user.uid) {
        const api = await getApiClient();
        const audioSurahs: IAudioSurah[] = [];
        for (const suraGuid of data.items) {
          const { data } = await api.getAudioSurahByGuid(suraGuid);
          audioSurahs.push(data.audioSurah);
        }
        setCurrentPlaylist({
          ...data,
          id: documentData.id,
          audioSurahs,
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createPlaylist = async ({
    user,
    playlistName,
  }: ICreatePlaylistEffect) => {
    try {
      const { db, addDoc, collection } = await getFirestoreClient();
      const playlist: ICreatePlaylist = {
        items: [],
        name: playlistName,
        ownerID: user.uid,
        ownerImage: user.photoURL ? user.photoURL : '',
        ownerName: `${user.displayName}`,
      };
      const collectionRef = collection(
        db,
        FIRESTORE_COLLECTIONS.SHARED_PLAYLISTS
      );
      await addDoc(collectionRef, playlist);
      await loadPlaylists(user);
    } catch (error) {
      console.error(error);
    }
  };

  const deletePlaylist = async ({
    user,
    playlistId,
  }: IBasePlaylistDetailEffect) => {
    try {
      const { db, deleteDoc, doc, getDoc } = await getFirestoreClient();
      const docRef = playlistDocRef(db, doc, playlistId);
      const documentData = await getDoc(docRef);
      const data = documentData.data() as IPlaylist;
      if (documentData && data.ownerID === user.uid) {
        await deleteDoc(docRef);
        await loadPlaylists(user);
      } else {
        throw new Error();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const addToPlaylist = async ({
    user,
    playlistId,
    audioSurah,
  }: IBasePlaylistItemsUpdateEffect) => {
    try {
      const { db, doc, getDoc, updateDoc } = await getFirestoreClient();
      const docRef = playlistDocRef(db, doc, playlistId);
      const documentData = await getDoc(docRef);
      const data = documentData.data() as IPlaylist;
      if (documentData && data.ownerID === user.uid) {
        const playlist: any = {
          ...data,
          items: getUniqueElements([...data.items, audioSurah.guid]),
        } as IPlaylist;
        await updateDoc(docRef, playlist);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const removeFromPlaylist = async ({
    user,
    playlistId,
    audioSurah,
  }: IBasePlaylistItemsUpdateEffect) => {
    try {
      const { db, doc, getDoc, updateDoc } = await getFirestoreClient();
      const docRef = playlistDocRef(db, doc, playlistId);
      const documentData = await getDoc(docRef);
      const data = documentData.data() as IPlaylist;
      if (documentData && data.ownerID === user.uid) {
        const playlist: any = {
          ...data,
          items: removeElementsFromArray(data.items, audioSurah.guid),
        } as IPlaylist;
        await updateDoc(docRef, playlist);
        await getPlaylistDetail({ user, playlistId });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const value = {
    playlist: playlistState,
    loadPlaylists,
    getPlaylistDetail,
    createPlaylist,
    deletePlaylist,
    addToPlaylist,
    removeFromPlaylist,
  };

  return (
    <PlaylistContext.Provider value={value}>
      {children}
    </PlaylistContext.Provider>
  );
};

export const usePlaylist = () =>
  useContext(PlaylistContext) as IPlaylistContext;

export default PlaylistProvider;
